/**
 * Tinted card at the top of the topic risk drawer showing the resulting risk level for the topic.
 */
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import { MethodologyLink, MethodologySections } from '@app/src/pages/TopicalRisk/Drawer/MethodologyLink'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import { getRiskWeakerColor } from '@app/src/utils/riskStatusHelpers'
import { Paper, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

function getDescriptionId(hasAssessmentResult: boolean, mayBeCappedAtInherentRisk: boolean): string {
  if (!hasAssessmentResult) return 'topicalRisk.drawer.topicRiskDescriptionInherentOnly'
  return mayBeCappedAtInherentRisk
    ? 'topicalRisk.drawer.topicRiskDescriptionCappedByAiScreening'
    : 'topicalRisk.drawer.topicRiskDescription'
}

export interface TopicRiskSummaryCardProps {
  riskStatus: RiskStatus
  hasAssessmentResult: boolean
  mayBeCappedAtInherentRisk: boolean
}

export const TopicRiskSummaryCard: React.FC<TopicRiskSummaryCardProps> = ({
  riskStatus,
  hasAssessmentResult,
  mayBeCappedAtInherentRisk,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  return (
    <Paper elevation={0} sx={{ backgroundColor: getRiskWeakerColor(theme, riskStatus), borderRadius: 2, p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="subtitle1">{formatMessage({ id: 'topicalRisk.drawer.topicRisk' })}</Typography>
          <SimpleRiskChip riskStatus={riskStatus} larger />
        </Stack>
        <Typography variant="body1" color="text.secondary">
          {formatMessage(
            {
              id: getDescriptionId(hasAssessmentResult, mayBeCappedAtInherentRisk),
            },
            {
              link: (chunks: React.ReactNode) => (
                <MethodologyLink section={MethodologySections.TopicRisk}>{chunks}</MethodologyLink>
              ),
            },
          )}
        </Typography>
      </Stack>
    </Paper>
  )
}

export default TopicRiskSummaryCard
